import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  IconButton,
  MenuList,
  MenuItem,
  Popper,
  Grow,
  ClickAwayListener,
  Paper,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Measure } from "@madie/madie-models";
import ShareIcon from "../../../../common/ShareIcon";

interface PropTypes {
  measures: Measure[];
  onClick: (option: string) => void;
  isOwner: boolean;
}

export const NOTHING_SELECTED = "Select a measure to share/unshare";
export const INVALID_SHARE_MEASURE =
  "You cannot share/unshare a measure you do not own";
export const VALID_SHARE_MEASURE = "Share/Unshare";

const options = ["Share With", "Unshare"];

export default function ShareAction(props: PropTypes) {
  const { measures, isOwner } = props;
  const [disableShareBtn, setDisableShareBtn] = useState(true);
  const [tooltipMessage, setTooltipMessage] = useState(NOTHING_SELECTED);
  const [open, setOpen] = useState(false);
  // move anchorElement to a stable reference that does not change across renders.
  const anchorRef = useRef<HTMLButtonElement>(null);
  const validateShareActionState = useCallback(() => {
    setDisableShareBtn(true);

    if (measures?.length === 0) {
      setTooltipMessage(NOTHING_SELECTED);
    } else if (isOwner) {
      setDisableShareBtn(false);
      setTooltipMessage(VALID_SHARE_MEASURE);
    } else {
      setTooltipMessage(INVALID_SHARE_MEASURE);
    }
  }, [measures, isOwner]);

  useEffect(() => {
    validateShareActionState();
  }, [measures, validateShareActionState]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setTooltipMessage(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMenuItemClick = (option: string) => {
    handleClose();
    props.onClick(option);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
    if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <Tooltip
      data-testid="share-action-tooltip"
      title={tooltipMessage}
      onMouseOver={validateShareActionState}
      placement="top"
      arrow
    >
      <span>
        <IconButton
          onClick={handleClick}
          disabled={disableShareBtn}
          data-testid="share-action-btn"
          ref={anchorRef}
        >
          <ShareIcon />
        </IconButton>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: "left top",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="share-menu"
                    onKeyDown={handleListKeyDown}
                  >
                    {options.map((option, i) => (
                      <MenuItem
                        data-testid={`${option}-option`}
                        key={option}
                        onClick={() => handleMenuItemClick(option)}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </span>
    </Tooltip>
  );
}
